import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { TimekeepingContext } from "../../../context/domain/timekeeping/TimekeepingContext";
import { checkTimesheetExists } from "../../../services/api/timekeeping";

import {
  formatDate,
  getDateInLocalTZFromDateStr,
  getDateString,
  today,
  getShiftedDate,
  getWeekStart,
} from "../../../utils/datetime";

import { decimalToHoursMinutes } from "../../../utils/datetime";
import { isPastCutoff } from "../../../utils/datetime";
import ShiftSummary from "./infoBoxes/ShiftSummary";

import { Button } from "../../../components/ui";
import { NavigationButton } from "../../../components/ui";
import StatusMessage from "../../../components/common/StatusMessage";

/**
 * WeeklyShiftSummary Component
 *
 * Displays a weekly summary of user shifts from Sunday to Saturday.
 * Shows shift data for days with completed shifts or allows users to clock in.
 *
 * Features:
 * - Weekly navigation with arrows
 * - Daily shift information display
 * - Total hours calculation for the week
 * - Visual indicators for shift stage
 * - Quick access to clock in/view shift details
 *
 * @param {Object} props - Component props
 * @param {Function} props.setEditShift - Function to set edit shift state
 * @param {Function} props.onClose - Function to handle close/back action
 * @returns {JSX.Element} The WeeklyShiftSummary component UI
 */
const WeeklyShiftSummary = ({ setEditShift }) => {
  // Context for accessing timekeeping state and user data
  const {
    setDate,
    weekStart,
    setWeekStart,
    weeklyShiftData,
    // Use message state from context
    message,
    setMessageDebounced,
    clearMessage,
  } = useContext(TimekeepingContext);
  const navigate = useNavigate();
  const [currentWeek, setCurrentWeek] = useState(0);
  const [editable, setEditable] = useState(true); // State to track if the shifts are still editable (Tuesday is the cutoff)

  const dateToday = today(); // Get today's date for comparison
  const weekEndDate = getShiftedDate(getDateInLocalTZFromDateStr(weekStart), 6);
  
  // Calculate currentWeek based on weekStart compared to today's week start
  useEffect(() => {
    if (weekStart) {
      const todayWeekStart = getWeekStart(dateToday, 0);
      const displayedWeekStart = getDateInLocalTZFromDateStr(weekStart);
      const daysDifference = Math.round((displayedWeekStart - todayWeekStart) / (1000 * 60 * 60 * 24));
      const weekOffset = Math.round(daysDifference / 7);
      setCurrentWeek(weekOffset);
    }
  }, [weekStart, dateToday]);

  const isEditable = (week) => {
    if (week === 0) {
      clearMessage(); // Clear message for the current week
      return true; // Always editable for the current week
    }
    // isPastCutoff function from timekeepingFunctions.js
    const pastCutoff = isPastCutoff(dateToday);

    if (pastCutoff || week < -1) {
      setMessageDebounced({
        type: "warning",
        text: `Shifts for this week are no longer editable. ${week === -1 ? "Payroll deadline is Tuesday." : ""}`,
      });
      return false; // Not editable after tuesday for last week or at all for weeks before last week
    } else {
      // Calculate the cutoff date to determine days remaining
      const cutoffDate = getShiftedDate(getWeekStart(dateToday, 0), 2);
      const daysRemaining = Math.ceil(
        (cutoffDate - dateToday) / (1000 * 60 * 60 * 24)
      );
      setMessageDebounced({
        type: "warning",
        text: `${daysRemaining} day${
          daysRemaining > 1 ? "s" : ""
        } remaining to submit timesheet.`,
      });
      if (daysRemaining === 0) {
        setMessageDebounced({
          type: "warning",
          text: `Last day to submit timesheet.`,
        });
      }
    }
    return !pastCutoff;
  };

  // Check if timesheet has been submitted for this week
  useEffect(() => {
    clearMessage();
    const checkSubmittedTimesheet = async () => {
      try {
        const weekEndDateString = getDateString(weekEndDate);
        const result = await checkTimesheetExists(weekEndDateString);

        if (result.exists && result.submitted) {
          setMessageDebounced({
            type: "success",
            text: "Timesheet submitted, thank you.",
          });
          setEditable(false);
        } else {
          // Only check editability if timesheet hasn't been submitted
          isEditable(currentWeek);
        }
      } catch (error) {
        console.error("Error checking timesheet:", error);
        // Fall back to regular editability check
        isEditable(currentWeek);
      }
    };

    checkSubmittedTimesheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, currentWeek]);

  // Navigate to previous week
  const goToPreviousWeek = () => {
    setEditable(isEditable(currentWeek - 1)); // Update editable state based on new week
    setCurrentWeek(currentWeek - 1);
    // Update weekStart to the previous week
    const previousWeekStart = getShiftedDate(
      getDateInLocalTZFromDateStr(weekStart),
      -7
    );
    setWeekStart(getDateString(previousWeekStart));
  };

  // Navigate to next week
  const goToNextWeek = () => {
    if (currentWeek < 0) {
      setEditable(isEditable(currentWeek + 1)); // Update editable state based on new week
      setCurrentWeek(currentWeek + 1);
      // Update weekStart to the next week
      const nextWeekStart = getShiftedDate(
        getDateInLocalTZFromDateStr(weekStart),
        7
      );
      setWeekStart(getDateString(nextWeekStart));
    }
  };

  // Determine if next week button should be disabled
  // We don't allow navigating to future weeks beyond the current one
  const isNextDisabled = currentWeek >= 0;

  // Get week label based on current offset
  const getWeekLabel = () => {
    if (currentWeek === 0) return "This Week";
    if (currentWeek === -1) return "Last Week";
    if (currentWeek < -1) return `${Math.abs(currentWeek)} Weeks Ago`;
    return `${currentWeek} Weeks Ahead`; // Unlikely to be used with isNextDisabled logic
  };

  return (
    weeklyShiftData && (
      <div className="bg-white rounded-lg shadow-md p-2 pt-4 mb-6">
        <h2 className="flex items-center text-lg text-black font-medium mb-4 px-2">
          {/* <Clock className="text-gray-700 mr-2" /> */}
          Timesheet:
          <b className="mx-1">{formatDate(weekStart, "month DD")}</b> -
          <b className="mx-1">
            {formatDate(getDateString(weekEndDate), "month DD, YYYY")}
          </b>
        </h2>

        {/* Week Navigation */}
        <div className="flex h-12 items-center text-black justify-between mb-4 bg-gray-200 rounded-lg">
          <NavigationButton
            direction="left"
            onClick={goToPreviousWeek}
            variant="secondary"
            className="rounded-l-md rounded-r-none h-12"
          />

          <span className="font-medium">
            {getWeekLabel()} {/* Display current week indicator */}
          </span>

          <NavigationButton
            direction="right"
            onClick={goToNextWeek}
            variant="secondary"
            disabled={isNextDisabled}
            className="rounded-r-md rounded-l-none h-12"
          />
        </div>

        {/* Status Message */}
        {message && <StatusMessage message={message} className="mb-4" />}

        {/* Daily Shifts */}
        <div className="space-y-2">
          {weeklyShiftData?.days.map((day, index) => {
            // If there are no shifts on the day, the api will always return a single shift with stage "No Shift"
            const hasShifts = day.shifts[0].stage !== "No Shift";
            return (
              <div
                key={index}
                className="bg-gray-100 overflow-hidden rounded-md"
              >
                {/* Day Header */}
                <div
                  className={`bg-gray-200 px-4 py-2 flex justify-between items-center`}
                >
                  <span
                    className={`${
                      getDateString(dateToday) === day.date
                        ? "font-bold"
                        : "font-bold"
                    } flex-1`}
                  >
                    {day.day} {formatDate(day.date, "month DD, YYYY")}
                    {getDateString(dateToday) === day.date && (
                      <span className="font-bold ml-2">(Today)</span>
                    )}
                  </span>
                  {hasShifts && day.shifts[0].stage !== "Missed Shift" && (
                    <span className="text-sm font-medium text-gray-700">
                      {day.totalHours && decimalToHoursMinutes(day.totalHours)}
                      {day.overtimeHours &&
                        ` (${decimalToHoursMinutes(day.overtimeHours)} OT)`}
                      {day.doubleTimeHours &&
                        ` (${decimalToHoursMinutes(day.doubleTimeHours)} DT)`}
                    </span>
                  )}
                </div>{" "}
                {/* Day Content - Map through array of shifts */}
                {day.shifts.map((shift, shiftIndex) => (
                  <div
                    key={`${index}-${shiftIndex}`}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <ShiftSummary
                      index={index}
                      shift={shift}
                      animate={false}
                      layout={shift.stage === "No Shift" ? "card" : "block"}
                      className={`${shiftIndex > 0 && day.shifts.length > 1 ? "mt-2" : ""}`}
                    />
                  </div>
                ))}{" "}
                {dateToday >= getDateInLocalTZFromDateStr(day.date) &&
                  editable && (
                    <>
                      {/* There will only ever be one type of shift on each day, so we can examine the [0] index stage */}
                      {day.shifts[0].stage === "Complete Shift" ? (
                        <Button
                          variant="primary"
                          text={
                            day.shifts.length > 1 ? "Edit Shifts" : "Edit Shift"
                          }
                          className="py-2 px-4 w-full h-8 rounded-t-none"
                          onClick={() => {
                            setDate(day.date);
                            setEditShift(true);
                          }}
                        />
                      ) : (
                        <Button
                          variant={
                            day.shifts[0].stage === "Missed Shift"
                              ? "pulseWarning"
                              : day.shifts[0].stage === "Current Shift"
                                ? "secondary"
                                : "custom"
                          }
                          text="Clock In"
                          className={`py-2 px-4 w-full rounded-t-none ${
                            day.shifts[0].stage === "No Shift"
                              ? "bg-gray-300 h-8 hover:bg-gray-400 text-black"
                              : "h-8"
                          }`}
                          onClick={() => {
                            navigate(`/timekeeping/clock-in/${day.date}`);
                          }}
                        />
                      )}
                    </>
                  )}
              </div>
            );
          })}
        </div>

        {/* Weekly Hours Summary */}
        <div className="mt-4 bg-gray-100 p-4 rounded-md">
          <h3 className="text-md font-bold mb-2">Weekly Hours Totals</h3>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-gray-600">Hours:</span>{" "}
              <span className="font-medium">
                {decimalToHoursMinutes(weeklyShiftData?.total_hours)}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Regular:</span>{" "}
              <span className="font-medium">
                {decimalToHoursMinutes(weeklyShiftData?.regular_hours)}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Overtime:</span>{" "}
              <span className="font-medium">
                {decimalToHoursMinutes(weeklyShiftData?.overtime_hours)}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Double Time:</span>{" "}
              <span className="font-medium">
                {decimalToHoursMinutes(weeklyShiftData?.double_time_hours)}
              </span>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <Button
          onClick={() => {
            navigate("/timekeeping/clock-in");
            clearMessage();
          }}
          variant={"secondary"}
          text={"View Today"}
          className="py-3 px-6 mt-6 w-full"
        />
      </div>
    )
  );
};

export default WeeklyShiftSummary;
